import React, { useEffect } from 'react';
import { Card, Form, Input, Button, Typography, Space, Spin, message } from 'antd';
import { UserOutlined, LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../../store';
import {
  fetchCaptcha,
  login as loginAction,
  clearAuthError,
} from '../../store/slices/authSlice';

const { Title, Text } = Typography;

interface LoginFormValues {
  username: string;
  password: string;
  captchaCode: string;
}

const Login: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [form] = Form.useForm<LoginFormValues>();

  const {
    captchaId,
    captchaImage,
    captchaLoading,
    loading,
    error,
    token,
  } = useSelector((state: RootState) => state.auth);

  const isAuthenticated = Boolean(token);

  useEffect(() => {
    dispatch(fetchCaptcha());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearAuthError());
      dispatch(fetchCaptcha());
      form.setFieldsValue({ captchaCode: '' });
    }
  }, [error, dispatch, form]);

  useEffect(() => {
    if (isAuthenticated) {
      message.success('登录成功');
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = (values: LoginFormValues) => {
    if (!captchaId) {
      message.warning('验证码加载中，请稍后重试');
      return;
    }

    dispatch(
      loginAction({
        username: values.username.trim(),
        password: values.password,
        captchaCode: values.captchaCode.trim(),
        captchaId,
      })
    );
  };

  const handleRefreshCaptcha = () => {
    dispatch(fetchCaptcha());
    form.setFieldsValue({ captchaCode: '' });
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          'linear-gradient(135deg, rgba(24,144,255,0.15), rgba(24,144,255,0.05))',
        padding: '32px 16px',
      }}
    >
      <Card
        style={{
          width: 420,
          maxWidth: '100%',
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
          borderRadius: 16,
        }}
        bodyStyle={{ padding: '40px 32px' }}
      >
        <Space
          direction="vertical"
          size={16}
          style={{ width: '100%', textAlign: 'center', marginBottom: 16 }}
        >
          <Title level={3} style={{ marginBottom: 0 }}>
            任务调度系统管理后台
          </Title>
          <Text type="secondary">请输入账号信息完成登录</Text>
        </Space>

        <Form<LoginFormValues>
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
        >
          <Form.Item
            label="用户名"
            name="username"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input
              size="large"
              prefix={<UserOutlined />}
              placeholder="请输入用户名"
              autoComplete="username"
            />
          </Form.Item>

          <Form.Item
            label="密码"
            name="password"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password
              size="large"
              prefix={<LockOutlined />}
              placeholder="请输入密码"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item label="验证码" required>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <Form.Item
                name="captchaCode"
                noStyle
                rules={[
                  { required: true, message: '请输入验证码' },
                  { len: 4, message: '验证码为4位字符' },
                ]}
              >
                <Input
                  size="large"
                  prefix={<SafetyCertificateOutlined />}
                  placeholder="请输入验证码"
                  autoComplete="off"
                />
              </Form.Item>
              <div
                style={{
                  flex: '0 0 auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '4px 0',
                  cursor: 'pointer',
                }}
                onClick={handleRefreshCaptcha}
              >
                {captchaLoading ? (
                  <Spin />
                ) : captchaImage ? (
                  <img
                    src={captchaImage}
                    alt="验证码"
                    style={{
                      display: 'block',
                      height: 52,
                    }}
                  />
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    点击刷新验证码
                  </Text>
                )}
              </div>
            </div>
          </Form.Item>

          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={loading}
            disabled={!captchaId}
            style={{ marginTop: 8 }}
          >
            登录
          </Button>
        </Form>
      </Card>
    </div>
  );
};

export default Login;
